import { Button, Group, Modal, NumberInput, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getInitialCashAmount,
  saveInitialCashAmount,
  saveInitialCashSkip,
} from '../../../utils/initialCash';
import { formatMoneyString } from '../../../utils/strings';

interface InitialCashPromptProps {
  opened: boolean;
  onClose: () => void;
  storeId: string;
  userId: string;
  currencySymbol?: string;
}

const InitialCashPrompt = ({
  opened,
  onClose,
  storeId,
  userId,
  currencySymbol = '$',
}: InitialCashPromptProps) => {
  const { t } = useTranslation(['Attendance', 'CommonButtons', 'FieldName']);
  const [amount, setAmount] = useState<number | ''>('');
  const [pendingAmount, setPendingAmount] = useState<number | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storageContext = {
    storeId,
    userId,
  };

  useEffect(() => {
    if (!opened || !storeId || !userId) {
      return;
    }

    const savedAmount = getInitialCashAmount(storageContext);
    setAmount(typeof savedAmount === 'number' ? savedAmount : '');
    setPendingAmount(null);
    setIsConfirming(false);
    setError(null);
  }, [opened, storeId, userId]);

  const handleSave = () => {
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) {
      setError(t('Attendance:initialCashPrompt.invalidAmount'));
      return;
    }

    setPendingAmount(amount);
    setIsConfirming(true);
    setError(null);
  };

  const handleConfirmSave = () => {
    if (pendingAmount === null) {
      return;
    }

    saveInitialCashAmount(storageContext, pendingAmount);
    setAmount('');
    setPendingAmount(null);
    setIsConfirming(false);
    setError(null);
    onClose();
  };

  const handleCancelConfirmation = () => {
    setPendingAmount(null);
    setIsConfirming(false);
  };

  const handleSkip = () => {
    saveInitialCashSkip(storageContext);
    setAmount('');
    setPendingAmount(null);
    setIsConfirming(false);
    setError(null);
    onClose();
  };

  if (!opened || !storeId || !userId) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isConfirming
          ? t('Attendance:initialCashPrompt.confirmTitle')
          : t('Attendance:initialCashPrompt.title')
      }
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
    >
      {isConfirming ? (
        <>
          <Text mb="sm">{t('Attendance:initialCashPrompt.confirmDescription')}</Text>
          <Text fw={700} size="xl">
            {formatMoneyString(pendingAmount, 2, '.', ',', currencySymbol)}
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleCancelConfirmation}>
              {t('CommonButtons:cancel')}
            </Button>
            <Button onClick={handleConfirmSave}>{t('CommonButtons:confirm')}</Button>
          </Group>
        </>
      ) : (
        <>
          <Text mb="md">{t('Attendance:initialCashPrompt.description')}</Text>
          <NumberInput
            value={amount}
            onChange={(value) => {
              setAmount(value as number | '');
              if (error) {
                setError(null);
              }
            }}
            allowDecimal
            decimalScale={2}
            min={0}
            required
            label={t('FieldName:startingCash')}
            placeholder={t('FieldName:startingCash')}
            error={error}
          />
          <Group justify="flex-end" mt="md">
            {/* DISABLED FOR NOW  */}
            {/* <Button variant="default" onClick={handleSkip}>
              {t('Attendance:initialCashPrompt.skip')}
            </Button> */}
            <Button onClick={handleSave}>{t('CommonButtons:save')}</Button>
          </Group>
        </>
      )}
    </Modal>
  );
};

export default InitialCashPrompt;
